import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/core/auth/auth';
import type { Json } from '@/integrations/supabase/types';

export interface AuditLogEntry {
  id: string;
  user_id: string;
  action: string;
  details: Record<string, unknown>;
  ip_masked: string | null; // Anonymized IP (e.g., 192.168.1.0)
  ip_address: string | null; // Deprecated: always null for privacy
  user_agent: string | null;
  created_at: string;
}

export function useAuditLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async (limit = 50) => {
    if (!user) {
      setLogs([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (fetchError) throw fetchError;
      setLogs((data || []) as AuditLogEntry[]);
    } catch (err) {
      console.error('Error fetching audit logs');
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const logAction = useCallback(async (
    action: string, 
    details: Record<string, unknown> = {}
  ) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { error: insertError } = await supabase
        .from('audit_logs')
        .insert([{
          user_id: user.id,
          action,
          details: details as Json,
          user_agent: navigator.userAgent,
        }]);

      if (insertError) throw insertError;
      
      // Refresh logs
      fetchLogs();
      return { error: null };
    } catch (err) {
      console.error('Error logging action');
      return { error: err as Error };
    }
  }, [user, fetchLogs]);

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      'sign_in': 'Signed in',
      'sign_out': 'Signed out',
      'report_finalized': 'Report finalized',
      'report_exported': 'Report exported',
      'settings_updated': 'Settings updated',
      'password_changed': 'Password changed',
      'data_exported': 'Data exported',
    };
    return labels[action] || action;
  };

  const exportLogs = (): string => {
    return JSON.stringify(logs, null, 2);
  };

  return {
    logs,
    loading,
    error,
    refreshLogs: fetchLogs,
    logAction,
    getActionLabel,
    exportLogs,
  };
}
